/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import { Inject, Injectable } from '@angular/core';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

// Our custom components we want the dialog to render:
import { clone, defaults } from 'lodash-es';
import { AudioRecordComponent } from '../../app/audio/audio-record/audio-record.component';
import { MacmillanBookDialogComponent } from '../../app/macmillan-book-dialog/macmillan-book-dialog.component';
import { Resume } from '../../resume/resume.model';
import { ResumeContactInfoDialogComponent } from '../../resume/contact-info-dialog/contact-info-dialog.component';
import { PageSnapshotDialogComponent } from '../../app/page-snapshot-dialog/page-snapshot-dialog.component';
import { CreateNewLinkDialogComponent } from
  '../../app/share-panel/private-share-link/create-new-link-dialog/create-new-link-dialog.component';
import { DialogConfig } from './types';
import { DialogComponent } from './dialog/dialog.component';
import { ButtonStyle } from '../aero-button/aero-button.component';
import { TranslationService } from '../../common/translation.service';
import { GroupInviteDialogComponent } from '../../app/group-invite-dialog/group-invite-dialog.component';
import { GettingStartedTemplatesDialogComponent } from
  '../../app/getting-started-templates-dialog/getting-started-templates-dialog.component';
import { ShareLinkDialogComponent } from '../../app/share-link-dialog/share-link-dialog.component';
import {
  CopyTemplateDialogComponent,
} from '../../app/organization/copy-template-dialog/copy-template-dialog.component';
import { TagsDialogComponent } from '../../app/organization/tags-dialog/tags-dialog.component';
import { TagsStudentDialogComponent } from '../../app/organization/tags-student-dialog/tags-student-dialog.component';

/**
 * The default configuration for standard aero dialogs.
 */
const defaultDialogConfigs : DialogConfig = {
  // Settings specific to the aero standard dialog
  primaryLabel: 'Submit',
  primaryStyle: ButtonStyle.PRIMARY,
  primaryDisabled: false,
  closeOnPrimary: true,
  secondaryStyle: ButtonStyle.SECONDARY,
  secondaryDisabled: false,
  closeOnSecondary: true,
  showSecondary: true,
  showFooter: true,
  showInputField: false,

  // Settings for primeng dynamic dialog
  width: '352px',
  closable: true,
  closeOnEscape: true,
  dismissableMask: true,

  // This hides the standard primeng header so that we can use our own header
  showHeader: false,
};

/**
 * Service for our Aero Dynamic Dialog.
 */
@Injectable()
export class AeroDialogService {
  /**
   * Width of dialog for mobile devices.
   */
  private readonly mobileWidth = '97%';

  /**
   * Width of dialog for non-mobile devices.
   */
  private readonly laptopWidth = '352px';

  /**
   * Constructor that injects primengs dialog service.
   *
   * @param dialogService Primeng dialogService.
   * @param BulbBrowserService Our AngularJS BulbBrowserService.
   * @param BulbAuthService Our AngularJS BulbAuthService.
   * @param $translate Translation service which is internally using Angularjs translation.
   * @param dynamicDialogRef Reference to the current open dialog.
   */
  constructor(
    private dialogService : DialogService,
    @Inject('BulbBrowserService') private BulbBrowserService : any,
    @Inject('BulbAuthService') private BulbAuthService : any,
    private $translate: TranslationService,
    private dynamicDialogRef: DynamicDialogRef,
  ) {}

  /**
   * Create a component factory resolver for the passed in angular template, rendering /compiling it in the DOM.
   *
   * @param component Passed in Angular component.
   * @param dialogOptions Object of dialogOptions.
   */
  show(component : any, dialogOptions : any) : DynamicDialogRef {
    return this.dialogService.open(component, dialogOptions);
  }

  /**
   * Create and show a standard aero dialog.  See DialogConfig for example usage and config properties.
   *
   * @param config Configuration for the standard dialog.
   */
  showDialog(config : DialogConfig) {

    // Make a copy of the config, so we can manipulate it.
    const configClone = clone(config);

    // Update the config with our standard defaults for any values not specified.
    defaults(configClone, defaultDialogConfigs);

    const ref = this.dialogService.open(DialogComponent, configClone);

    const sub = ref.onClose.subscribe(() => {
      sub.unsubscribe();

      if (configClone.onClose) {
        configClone.onClose(configClone);
      }

      if (configClone.focusAfterClosed) {
        configClone.focusAfterClosed.focus();
      }
    });
    return ref;
  }

  /**
   * Displays an error dialog with the specified detail message.
   *
   * @param {string} errorDetail - The detailed error message to show in the dialog.
   */
  showErrorDialog(errorDetail: string) {
    this.showDialog({
      icon: 'fa fa-exclamation-triangle',
      iconClass: 'aero-alert',
      title: this.$translate.translateInstant('use_template_error_modal_title'),
      detail: errorDetail,
      closable: true,
      primaryLabel: this.$translate.translateInstant('button_ok'),
      showSecondary: false,
    });
  }

  /**
   * Helper method to get the enum for Javascript users of this class.
   */
  // eslint-disable-next-line class-methods-use-this
  public getButtonStyleEnum() {
    return ButtonStyle;
  }

  /**
   * Show the specific dialog for the MacMillan 3 Book dialog.
   *
   * @param runningInAngularJS Boolean indicating if the modal is being opened from our AngularJS framework.
   * @param redirectToGroup Boolean indicating if the user is going to be redirected after the creation of the
   *   MacMillan group.
   *
   * Important Note: We are making a specific function for this workflow due to the difficulties of dynamically
   * passing a component reference between the Angular and AngularJS framework and vice versa. This is a *temporary*
   * work around, until we figure out a more intelligent way of doing this. For instance, possibly pass the name
   * of the component as a string to the show() method above, and dynamically create the component that way.
   */
  showMacMillanBookDialog(
    runningInAngularJS : boolean = false,
    redirectToGroup : boolean = false,
  ) : DynamicDialogRef {
    const isMobile = this.BulbBrowserService.isIOSMobile() || this.BulbBrowserService.isAndroid();
    /**
     * Dialog options for the MacMillan 3 Book dialog.
     */
    const macMillanDialogOptions = {
      width: (!isMobile) ? '40%' : this.mobileWidth,
      height: '437px',
      dismissableMask: true,
      styleClass: 'aero-dialog',
      runningInAngularJS,
      redirectToGroup,
    };
    return this.dialogService.open(MacmillanBookDialogComponent, macMillanDialogOptions);
  }

  /**
   * Show the dialog for inviting multiple people to a group.
   *
   * @param runningInAngularJS Boolean indicating if the modal is being opened from our AngularJS framework.
   * @param invitedUsers list of users to add as a member to the group.
   * @param members list of members in the group.
   * @param groupId of the group we are adding members to.
   *
   * Important Note: We are making a specific function for this workflow due to the difficulties of dynamically
   * passing a component reference between the Angular and AngularJS framework and vice versa. This is a *temporary*
   * work around, until we figure out a more intelligent way of doing this. For instance, possibly pass the name
   * of the component as a string to the show() method above, and dynamically create the component that way.
   */
  showGroupInviteDialog(
    groupId : string,
    invitedUsers : any,
    members : any,
    runningInAngularJS : boolean = false,
  ) : DynamicDialogRef {
    const groupDialogOptions = {
      width: this.laptopWidth,
      dismissableMask: true,
      styleClass: 'group-invite-dialog aero-dialog',
      runningInAngularJS,
      invitedUsers,
      members,
      groupId,
    };
    return this.dialogService.open(GroupInviteDialogComponent, groupDialogOptions);
  }

  /**
   * Show the dialog for getting started user templates.
   *
   * @param position of the current account.
   * @param runningInAngularJS Boolean indicating if the modal is being opened from our AngularJS framework.
   *
   * Important Note: We are making a specific function for this workflow due to the difficulties of dynamically
   * passing a component reference between the Angular and AngularJS framework and vice versa. This is a *temporary*
   * work around, until we figure out a more intelligent way of doing this. For instance, possibly pass the name
   * of the component as a string to the show() method above, and dynamically create the component that way.
   */
  showGettingStartedTemplateDialog(
    position,
    runningInAngularJS : boolean = false,
  ) : DynamicDialogRef {
    const startingTemplatesDialogOptions = {
      width: this.laptopWidth,
      dismissableMask: false,
      styleClass: 'starting-templates-dialog aero-dialog',
      position,
      runningInAngularJS,
    };
    return this.dialogService.open(GettingStartedTemplatesDialogComponent, startingTemplatesDialogOptions);
  }

  /**
   * Show the specific dialog for our audio recording Feature.
   *
   * Important Note: We are making a specific function for this workflow due to the difficulties of dynamically
   * passing a component reference between the Angular and AngularJS framework and vice versa. This is a *temporary*
   * work around, until we figure out a more intelligent way of doing this. For instance, possibly pass the name
   * of the component as a string to the show() method above, and dynamically create the component that way.
   *
   * @param runningInAngularJS Boolean indicating if the modal is being opened from our AngularJS framework.
   * @param fromWithinAssetlibrary Boolean indicating if the modal is being opened from within our Assetlibrary.
   */
  showAudioRecordingDialog(
    runningInAngularJS : boolean = false,
    fromWithinAssetlibrary : boolean = false,
  ) : DynamicDialogRef {
    const isMobile = this.BulbBrowserService.isIOSMobile() || this.BulbBrowserService.isAndroid();
    /**
     * Options for the Audio Recording dialog.
     */
    const audioRecordingDialogOptions = {
      // The width here needs to be in sync with the value specified in the audio-record.component.scss file.
      width: (!isMobile) ? '576px' : '377px',
      height: '369px',
      closeOnEscape: true,
      closable: false,
      styleClass: 'audio-record aero-dialog',
      runningInAngularJS,
      fromWithinAssetlibrary,
    };
    return this.dialogService.open(AudioRecordComponent, audioRecordingDialogOptions);
  }

  /**
   * Show the specific dialog for the Resumé Contact Info.
   *
   * @param resume object of the author.
   */
  showResumeContactInfoDialog(resume: Resume) : DynamicDialogRef {
    /**
     * Options for the Resumé Contact Info dialog.
     */
    const resumeContactInfoDialogOptions = {
      width: this.laptopWidth,
      closeOnEscape: true,
      closable: true,
      dismissableMask: true,
      styleClass: 'resume-contact-dialog aero-dialog',
      data: { resume },
    };
    return this.dialogService.open(ResumeContactInfoDialogComponent, resumeContactInfoDialogOptions);
  }

  /**
   * Show the specific dialog for the Resume or portfolio Share Link.
   *
   * @param account Current user account.
   * @param runningInAngularJS Boolean indicating if the modal is being opened from our AngularJS framework.
   * @param showResumeShareModal Indicates whether the Resume Share modal is active.
   */
  showShareLinkDialog(
    account : any,
    runningInAngularJS: boolean = false,
    showResumeShareModal: boolean = true,
  ) : DynamicDialogRef {
    const resumeShareLinkDialogOptions = {
      width: this.laptopWidth,
      height: '440px',
      closeOnEscape: true,
      closable: true,
      dismissableMask: true,
      styleClass: 'resume-share-link-dialog aero-dialog',
      runningInAngularJS,
      account,
      data: {
        showResumeShareModal,
      },
    };

    return this.dialogService.open(ShareLinkDialogComponent, resumeShareLinkDialogOptions);
  }

  /**
   * Show the dialog for the Create New Link prompt in the Share Link component.
   *
   * @param unitId id of unit for which the link is being created.
   */
  showCreateNewLinkDialog(unitId : string) : DynamicDialogRef {
    /**
     * Options for the Create New Link dialog.
     */
    const createNewLinkDialogOptions = {
      unitId,
      width: '352px',
      closeOnEscape: false,
      closable: true,
      dismissableMask: true,
      styleClass: 'resume-share-link-dialog updated-modal-body bulb-create-new-link-dialog aero-dialog',
      data: { unitId },
    };
    return this.dialogService.open(CreateNewLinkDialogComponent, createNewLinkDialogOptions);
  }

  /**
   * Show the dialog for page snapshot feature.
   */
  showSnapshotDialog(unitId : string, draftPageId : string) : DynamicDialogRef {
    const pageSnapshotOptions = {
      unitId,
      draftPageId,
      width: this.laptopWidth,
      height: '568px',
      closeOnEscape: true,
      dismissableMask: true,
      styleClass: 'snapshot-dialog aero-dialog',
    };

    return this.dialogService.open(PageSnapshotDialogComponent, pageSnapshotOptions);
  }

  /**
   * Show the dialog for copy template.
   */
  showCopyTemplateDialog(organizationId: string) : DynamicDialogRef {
    const copyTemplateOptions = {
      width: 'calc(100vw - 1rem)',
      height: 'auto',
      breakpoints: {
        '960px': '92vw',
        '640px': '98vw',
      },
      contentStyle: { overflow: 'auto', padding: '1.875rem' },
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: false,
      styleClass: 'copy-template-dialog aero-dialog',
      data: { organizationId },
    };

    return this.dialogService.open(CopyTemplateDialogComponent, copyTemplateOptions);
  }

  /**
   * Show the dialog for copy template.
   */
  showTagsDialog(organizationId: string, tag?: any, isOrgTag: boolean = true) : DynamicDialogRef {
    const copyTemplateOptions = {
      width: '25rem',
      height: 'auto',
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: false,
      styleClass: 'aero-dialog',
      data: { organizationId, tag, isOrgTag },
    };

    return this.dialogService.open(TagsDialogComponent, copyTemplateOptions);
  }

  /**
   * Show the dialog for copy template.
   */
  showStudentTagsDialog(organizationId?: string, unit?:any) : DynamicDialogRef {
    const copyTemplateOptions = {
      width: '30rem',
      height: '40rem',
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: false,
      styleClass: 'aero-dialog student-dialog',
      data: { organizationId, unit },
    };

    return this.dialogService.open(TagsStudentDialogComponent, copyTemplateOptions);
  }

  /**
   * Remove the dialog from the DOM for AngularJS framework.
   * The component that registers the initial opening of the dialog can't seem to close it for AngularJS framework.
   */
  // TODO: Revise this logic to close the specific dialog as this will close ALL dialogs currently open.
  // eslint-disable-next-line class-methods-use-this
  removeDialog() {
    jQuery('p-dynamicdialog').remove();
  }

  closeDialog() {
    this.dynamicDialogRef.close();
  }

  /**
   * Dialog for upgrade plan.
   *
   * @param detail Translation key for detail.
   */
  showPlanUpgradeDialog(data?: any) {
    this.showDialog({
      svgIconName: 'b-plus-icon',
      title: data?.title || this.$translate.translateInstant('premium_feature'),
      detail: data?.detail || this.$translate.translateInstant('resume_premium_feature'),
      primaryLabel: this.$translate.translateInstant('button_upgrade'),
      iconClass: 'aero-highlight',
      styleClass: 'aero-bulb-plus-icon',
      onPrimary: () => {
        this.BulbAuthService.planlistState();
      },
    });
  }

  /**
   * Dialog for organization suspension alert. If called display the suspension dialog.
   * Else keep the dialog closed.
   */
  showOrgSuspended() {
    this.showDialog({
      detail: this.$translate.translateInstant('organization_suspended'),
      primaryLabel: this.$translate.translateInstant('close_dialog'),
      closeOnPrimary: true,
      showSecondary: false,
    });
  }
}
